import React, { useEffect, useRef, useState } from 'react'
import { Button, ButtonRounded, WContainer, WLink } from '@components/atoms'
import { Close } from '@components/icons'
import LogoEmilamerica from '@components/icons/LogoEmilamerica'
import { LandingPage } from '@components/organisms'
import { PaginaNode } from '@gql/graphql'
import { AudioDescrizioneObject, AudioTrack } from '@utils/types'
import styles from './AudioDescrizioneTemplate.module.sass'
import useTrans from '@hooks/useTrans'

interface Props {
  pagina?: PaginaNode
  audioDescrizione?: AudioDescrizioneObject
}

const AudioDescrizioneTemplate = (props: Props) => {
  const { audioDescrizione, pagina } = props
  const tracks: AudioTrack[] = (audioDescrizione?.audioFiles || [])
    .filter((item) => !!item?.fileUrl)
    .map((item, index) => ({
      id: item.pk || index + 1,
      label: item.cta,
      url: item.fileUrl,
    }))
  const [activeTrack, setActiveTrack] = useState<AudioTrack | null>(null)
  const [isClosingPlayer, setIsClosingPlayer] = useState(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const t = useTrans()

  const openPlayer = (track: AudioTrack) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setIsClosingPlayer(false)
    setActiveTrack(track)
  }

  const handleClosePlayer = () => {
    if (!activeTrack || isClosingPlayer) return
    setIsClosingPlayer(true)
    closeTimeoutRef.current = setTimeout(() => {
      setActiveTrack(null)
      setIsClosingPlayer(false)
      closeTimeoutRef.current = null
    }, 320)
  }

  useEffect(() => {
    if (activeTrack) {
      document.body.classList.add('w-noscroll')
    } else {
      document.body.classList.remove('w-noscroll')
    }

    return () => document.body.classList.remove('w-noscroll')
  }, [activeTrack])

  useEffect(() => {
    if (!activeTrack) return () => {}

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClosePlayer()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeTrack, isClosingPlayer])

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    },
    []
  )

  return (
    audioDescrizione?.collezione && (
      <LandingPage pagina={pagina} className={`${styles.root}`}>
        <main className={styles.page}>
          <header className={styles.header}>
            {/* <WLink
              href={'/'}
              className={styles.logo}
              ariaLabel={t('Logo')}
            >
              <LogoEmilamerica className={styles.logo} />
            </WLink> */}
            <LogoEmilamerica className={styles.logo} />
          </header>
          <WContainer variant="xs" className={styles.content}>
            <h1 className={styles.title}>{audioDescrizione.collezione?.title}</h1>
            <div className={styles.buttons}>
              {tracks.map((track) => (
                <Button
                  key={track.id}
                  label={track.label}
                  variant="ghost"
                  className={`${styles.button} ${
                    activeTrack?.id === track.id ? styles['button--active'] : ''
                  }`}
                  onClick={() => openPlayer(track)}
                />
              ))}
            </div>
          </WContainer>
          {activeTrack && (
            <div
              className={`${styles.playerOverlay} ${
                isClosingPlayer ? styles['playerOverlay--closing'] : ''
              }`}
              aria-label={t('Close audio player')}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                if (event.target === event.currentTarget) handleClosePlayer()
              }}
              onKeyDown={(event) => {
                if (
                  event.target === event.currentTarget &&
                  (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault()
                  handleClosePlayer()
                }
              }}
            >
              <div
                className={`${styles.playerWrap} ${
                  isClosingPlayer ? styles['playerWrap--closing'] : ''
                }`}
              >
                <audio
                  key={activeTrack.id}
                  className={styles.player}
                  controls
                  autoPlay
                  controlsList="nodownload"
                >
                  <source src={activeTrack.url} type="audio/mpeg" />
                  <track
                    kind="captions"
                    srcLang="en"
                    label={t('Trascrizione non disponibile')}
                    src="data:text/vtt,WEBVTT"
                  />
                  {t('Your browser not support audio element')}
                </audio>
                <ButtonRounded
                  icon={<Close />}
                  ariaLabel={t('Close audio player')}
                  size="xs"
                  theme="neutral20"
                  backgroundTheme="dark"
                  className={styles.playerClose}
                  onClick={handleClosePlayer}
                />
              </div>
            </div>
          )}
        </main>
      </LandingPage>
    )
  )
}

export default AudioDescrizioneTemplate
